import React, { useMemo } from "react";

import { FetchPriority } from "@/types/react-env";
import { applyImgParamsToUrl } from "@/utils/general";

export interface PictureSource {
  media: string;
  srcSet: string;
}

export interface PictureProps {
  src: string;
  sources?: PictureSource[];
  alt?: string;
  role?: string;
  pictureClassName?: string;
  className?: string;
  style?: React.CSSProperties;
  fetchPriority?: FetchPriority;
  width?: number;
  height?: number;
  loading?: "eager" | "lazy";
  ref?: React.Ref<HTMLImageElement>;
}

// --- Internal Helpers ---

const getAvifParams = (url: string) => {
  return url.includes("nutrafol.com") ? { format: "avif" } : { fm: "avif" };
};

const hasAvifParam = (url: string) => url.includes("=avif");

/**
 * 🩹 Repair and Validate Logic
 * 1. Strips fl=progressive from PNGs (illegal)
 * 2. Ensures fl=progressive for non-PNGs (optimization)
 * 3. Fixes '?' and '&' order
 */
const formatAndValidate = (url: string): string => {
  if (!url) return "";

  // Initial cleanup: remove progressive flag and trailing symbols
  let processedUrl = url.replace(/([?&])fl=progressive(&|$)/, "$1").replace(/[?&]$/, "");
  // Repair query string syntax
  const hasAmp = processedUrl.includes("&");
  if (hasAmp) {
    const qIdx = processedUrl.indexOf("?");
    const aIdx = processedUrl.indexOf("&");
    if (qIdx === -1 || aIdx < qIdx) {
      processedUrl = processedUrl.replace("&", "?");
    }
  }
  // Final Guard
  if (processedUrl.includes("&") && !processedUrl.includes("?")) {
    throw new Error(`Malformed URL: "${processedUrl}". Query strings must start with "?".`);
  }

  // PNG Check: If NOT a PNG, we actually WANT fl=progressive for the fallback
  const isPng = processedUrl.split("?")[0].toLowerCase().endsWith(".png");
  if (!isPng) {
    const separator = processedUrl.includes("?") ? "&" : "?";
    processedUrl += `${separator}fl=progressive`;
  }

  return processedUrl;
};

export default function Picture({
  src,
  sources = [],
  alt = "",
  role = "",
  pictureClassName = "",
  className = "",
  style,
  fetchPriority = "auto",
  width,
  height,
  loading = "lazy",
  ref,
}: PictureProps) {
  const effectiveRole = !alt ? role || "presentation" : undefined;

  /**
   * 1. Filter and Sort:
   * Only use sources that have a media query (Art Direction).
   * Sort DESCENDING (Largest width first) to fix cascading issues.
   */
  const mediaSources = useMemo(() => {
    return [...sources]
      .filter((source) => !!source.media)
      .sort((a, b) => {
        const valA = parseInt(a.media.match(/\d+/)?.[0] || "0", 10);
        const valB = parseInt(b.media.match(/\d+/)?.[0] || "0", 10);
        return valB - valA;
      });
  }, [sources]);

  // 2. Format the primary source (Standard JPG/PNG fallback)
  const finalSrc = useMemo(() => formatAndValidate(src), [src]);

  // 3. Generate the default AVIF source (Mobile/Global)
  const mainAvifSrc = useMemo(() => {
    const cleanBase = finalSrc.replace(/([?&])fl=progressive(&|$)/, "$1").replace(/[?&]$/, "");
    return hasAvifParam(cleanBase)
      ? cleanBase
      : applyImgParamsToUrl(cleanBase, getAvifParams(cleanBase));
  }, [finalSrc]);

  return (
    <picture className={pictureClassName}>
      {/* GROUP 1: AVIF Art Direction Overrides */}
      {mediaSources.map((source, index) => {
        const cleanSource = source.srcSet.replace(/([?&])fl=progressive(&|$)/, "$1");
        const avifSrcSet = hasAvifParam(cleanSource)
          ? cleanSource
          : applyImgParamsToUrl(cleanSource, getAvifParams(cleanSource));

        return (
          <source
            key={`avif-${index}`}
            media={source.media}
            srcSet={avifSrcSet}
            type="image/avif"
          />
        );
      })}
      {/* Default Global AVIF (Serves as mobile default or primary if no art direction) */}
      <source srcSet={mainAvifSrc} type="image/avif" />
      {/* GROUP 2: Standard Fallback Art Direction (Non-AVIF browsers) */}
      {mediaSources.map((source, index) => (
        <source
          key={`fallback-${index}`}
          media={source.media}
          srcSet={formatAndValidate(source.srcSet)}
        />
      ))}
      {/* 3. Legacy Fallback Image */}
      {/* eslint-disable-next-line no-restricted-syntax */}
      <img
        loading={loading}
        ref={ref}
        src={finalSrc}
        alt={alt}
        {...(effectiveRole && { role: effectiveRole })}
        className={className}
        style={style}
        fetchPriority={fetchPriority}
        width={width}
        height={height}
      />
    </picture>
  );
}
